import { Component, ReactNode } from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const STALE_CHUNK_RELOAD_FLAG = 'stale-chunk-reloaded';

function isStaleChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
    msg,
  );
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, _info: { componentStack: string }) {
    // Stale chunk after deploy: the tab holds references to asset hashes that
    // no longer exist. Reload once (guarded by sessionStorage so we don't
    // loop on a real persistent error).
    if (isStaleChunkError(error)) {
      if (!sessionStorage.getItem(STALE_CHUNK_RELOAD_FLAG)) {
        sessionStorage.setItem(STALE_CHUNK_RELOAD_FLAG, '1');
        window.location.reload();
        return;
      }
    }
    // Error is visible in this.state.error; Sentry captures it via the integration
  }

  render() {
    if (this.state.hasError) {
      // Suppress the error UI during a stale-chunk reload so the user sees a
      // blank flash rather than a scary dialog before the page reloads.
      if (isStaleChunkError(this.state.error)) {
        return null;
      }
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', p: 4 }}>
          <Card sx={{ maxWidth: 480, textAlign: 'center' }}>
            <CardContent sx={{ py: 4 }}>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>Something went wrong</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {this.state.error?.message || 'An unexpected error occurred.'}
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.reload();
                }}
              >
                Reload Page
              </Button>
            </CardContent>
          </Card>
        </Box>
      );
    }
    return this.props.children;
  }
}
